
import { useState, useEffect } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { useAdvancedAnalysis } from '@/hooks/useAdvancedAnalysis';
import { motion } from 'framer-motion';
import { AnalysisCardHeader } from './enhanced-analysis-card/AnalysisCardHeader';
import { AnalysisCardInfo } from './enhanced-analysis-card/AnalysisCardInfo';
import { AnalysisCardProgress } from './enhanced-analysis-card/AnalysisCardProgress';
import { AnalysisCardBalance } from './enhanced-analysis-card/AnalysisCardBalance';
import { AnalysisCardButton } from './enhanced-analysis-card/AnalysisCardButton';

interface EnhancedAdvancedAnalysisCardProps {
  title: string;
  description: string;
  cost: number;
  data: any;
  functionName: string;
  onAnalysisComplete: (analysis: any) => void;
  className?: string;
  estimatedTime?: string;
  features?: string[];
}

export default function EnhancedAdvancedAnalysisCard({
  title,
  description,
  data,
  functionName,
  onAnalysisComplete,
  className = "",
  estimatedTime = "2-3 min",
  features = []
}: EnhancedAdvancedAnalysisCardProps) {
  const { isGenerating, userBalance, loadBalance, generateAnalysis } = useAdvancedAnalysis();
  const [progress, setProgress] = useState(0);
  const [currentStep, setCurrentStep] = useState('');

  useEffect(() => {
    loadBalance();
  }, [loadBalance]);

  useEffect(() => {
    if (isGenerating) {
      const steps = [
        'Analisando dados de personalidade...',
        'Aplicando modelos psicológicos...',
        'Gerando insights personalizados...',
        'Criando recomendações...',
        'Finalizando análise...'
      ];

      let stepIndex = 0;
      setProgress(0);
      setCurrentStep(steps[0]);

      const interval = setInterval(() => {
        setProgress(prev => {
          const newProgress = prev + 20;
          if (newProgress <= 100) {
            if (stepIndex < steps.length - 1) {
              stepIndex++;
              setCurrentStep(steps[stepIndex]);
            }
            return newProgress;
          }
          return prev;
        });
      }, 800);

      return () => clearInterval(interval);
    }
  }, [isGenerating]);

  const handleGenerate = async () => {
    const result = await generateAnalysis(data, {
      cost: 30,
      functionName,
      minimumBalance: 30
    });

    if (result) {
      onAnalysisComplete(result);
    }
  };

  const canAfford = userBalance !== null && userBalance >= 30;
  const hasEnoughBalance = userBalance !== null && userBalance >= 60;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
      className={className}
    >
      <Card className="border-2 border-dashed border-purple-200 hover:border-purple-300 transition-all duration-300 hover:shadow-lg">
        <AnalysisCardHeader 
          title={title}
          description={description}
          isGenerating={isGenerating}
        />
        
        <CardContent className="space-y-4">
          <AnalysisCardInfo 
            estimatedTime={estimatedTime}
            features={features}
          />

          <AnalysisCardBalance 
            userBalance={userBalance}
            canAfford={canAfford}
            hasEnoughBalance={hasEnoughBalance}
          />

          <AnalysisCardProgress 
            isGenerating={isGenerating}
            progress={progress}
            currentStep={currentStep}
          />

          <AnalysisCardButton 
            isGenerating={isGenerating}
            canAfford={canAfford}
            onGenerate={handleGenerate}
          />

          <p className="text-xs text-center text-muted-foreground">
            Análise baseada em modelos psicológicos cientificamente validados
          </p>
        </CardContent>
      </Card>
    </motion.div>
  );
}
